import { inject, Injectable, effect } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { OrderRepository, OrderItem, OrderStateType, UserType } from 'sp-client';
import { SessionService } from '../session/session.service';
import {
  interval,
  startWith,
  switchMap,
  tap,
  Subject,
  of,
  combineLatest,
  map,
} from 'rxjs';
import { signal, computed } from '@angular/core';

// LocalStorage key for persisting filter settings
const ORDERS_FILTER_STORAGE_KEY = 'sp-orders-filter-settings';

// Interface for stored filter settings
interface OrderFilterSettings {
  showOutdated: boolean;
  showCurrent: boolean;
  showPreorders: boolean;
  showOnSite: boolean;
  sortBy: 'newest' | 'oldest' | 'pickupSoonest' | 'pickupLatest';
  selectedStates: OrderStateType[];
}

// Type representing unit for outdated threshold
type TimeUnit = 'minutes' | 'hours' | 'days';
export type OrderFilterType =
  | 'current'
  | 'past'
  | 'canceled'
  | 'open'
  | 'ready'
  | 'fetched'
  | 'unclaimed'
  | 'allStatesOn'
  | 'allStatesOff'
  | 'preorders'
  | 'onSite';

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  private orderRepository = inject(OrderRepository);
  private sessionService = inject(SessionService);

  private readonly refreshInterval = 30000; // 30 seconds

  // We need a way to manually trigger a refresh
  private manualRefresh = new Subject<void>();

  // Load saved settings from LocalStorage
  private savedSettings = this.loadFilterSettings();

  readonly showOutdated = signal(this.savedSettings.showOutdated);
  readonly showCurrent = signal(this.savedSettings.showCurrent);

  // Search text signal for filtering by customer name or order number
  readonly searchText = signal<string>('');

  // Order type filter: Preorders (with takeout_time) vs On-site/Table (without takeout_time)
  readonly showPreorders = signal(this.savedSettings.showPreorders);
  readonly showOnSite = signal(this.savedSettings.showOnSite);

  // Sorting
  readonly sortBy = signal<'newest' | 'oldest' | 'pickupSoonest' | 'pickupLatest'>(this.savedSettings.sortBy);

  readonly showAll = computed(() =>
    this.selectedStates().size === 5 // 5 states
  );

  readonly showNone = computed(() =>
    this.selectedStates().size === 0
  );

  readonly outdatedThreshold = signal<{ amount: number; unit: TimeUnit }>({
    amount: 1,
    unit: 'days',
  });

  readonly selectedStates = signal<Set<OrderStateType>>(new Set(this.savedSettings.selectedStates));

  // Store filter: null = all stores (admin view)
  readonly selectedStoreId = signal<number | null>(null);

  // Whether the current user can see all stores (admin types)
  readonly isMultiStoreAdmin = computed(() => {
    const userType = this.sessionService.user()?.user_type_id;
    return userType === UserType.BackendAdmin || userType === UserType.BackendUser || userType === UserType.SuperAdmin;
  });

  readonly filterOptionsOrderStateType = signal<{ label: string; value: OrderStateType; filter: OrderFilterType }[]>([
    { label: 'ordersPage.cancelledState', value: OrderStateType.Cancelled, filter: 'canceled' },
    { label: 'ordersPage.openState', value: OrderStateType.Open, filter: 'open' },
    { label: 'ordersPage.readyState', value: OrderStateType.Ready, filter: 'ready' },
    { label: 'ordersPage.takenOutState', value: OrderStateType.Fetched, filter: 'fetched' },
    { label: 'ordersPage.unclaimedState', value: OrderStateType.Unclaimed, filter: 'unclaimed' }
  ]);

  /**
   * Sets the outdated threshold for the orders list.
   * @param amount - The amount of time to consider an order outdated.
   * @param unit - The unit of time to consider an order outdated. Accepts 'minutes', 'hours', 'days'.
   */
  setOutdatedThreshold(amount: number, unit: TimeUnit) {
    this.outdatedThreshold.set({ amount, unit });
    // Immediately refresh list to apply new threshold
    this.refreshOrders();
  }

  /**
   * Returns the Date object representing the oldest date that is still
   * considered "fresh". Orders before that are outdated.
   */
  private calcCutoff(): Date {
    const { amount, unit } = this.outdatedThreshold();
    const cutoff = new Date();

    switch (unit) {
      case 'minutes':
        cutoff.setMinutes(cutoff.getMinutes() - amount);
        break;
      case 'hours':
        cutoff.setHours(cutoff.getHours() - amount);
        break;
      case 'days':
      default:
        cutoff.setDate(cutoff.getDate() - amount);
        break;
    }

    // normalise to full minutes for minutes/hours units? Not necessary.
    return cutoff;
  }

  /** Helper to obtain the relevant date of an order (takeout > created). */
  private getOrderRelevantDate(order: OrderItem): Date {
    return order.takeout_time
      ? new Date(order.takeout_time)
      : new Date(order.created_at);
  }

  private ordersRxResource = rxResource({
    params: () => ({
      hasUser: this.sessionService.user() != null,
      storeId: this.isMultiStoreAdmin()
        ? this.selectedStoreId() ?? undefined  // Admin: selected store or all
        : this.sessionService.user()?.store_id, // Employee: own store only
      showOutdated: this.showOutdated(),
      showPreorders: this.showPreorders(),
      showOnSite: this.showOnSite(),
      searchText: this.searchText(),
    }),
    stream: ({ params }) =>
      !params.hasUser
        ? of(undefined)
        : combineLatest([
            this.manualRefresh.pipe(startWith(undefined)), // Start immediately
            interval(this.refreshInterval).pipe(startWith(0)),
          ]).pipe(
            switchMap(() =>
              this.orderRepository
                .getOrders(
                  undefined,
                  undefined,
                  params.storeId,
                  false // Assuming this was for a different purpose, like 'includeArchived'
                )
                .pipe(
                  map((orders) => {
                    if (!orders) return undefined;
                    const cutoff = this.calcCutoff();

                    let filteredOrders = orders.filter((order) => {
                      // Only show successfully paid orders
                      if (order.payment_step !== 'Success') return false;

                      // Hide orders without any positions (nothing ordered)
                      if (!Array.isArray(order.positions) || order.positions.length === 0) return false;

                      const orderDate = this.getOrderRelevantDate(order);
                      const includePast = this.showOutdated();
                      const includeCurrent = this.showCurrent();

                      const isPast = orderDate < cutoff;
                      const dateMatches =
                        (isPast && includePast) || (!isPast && includeCurrent);

                      const stateMatches = this.selectedStates().has(
                        order.state_id
                      );

                      // Order type filter: preorders vs on-site
                      const isPreorder = order.takeout_time !== null;
                      const typeMatches =
                        (isPreorder && this.showPreorders()) ||
                        (!isPreorder && this.showOnSite());

                      return dateMatches && stateMatches && typeMatches;
                    });

                    // Apply search filter
                    const searchLower = this.searchText().toLowerCase().trim();
                    if (searchLower) {
                      filteredOrders = filteredOrders.filter(order =>
                        order.ordernumber?.toLowerCase().includes(searchLower) ||
                        order.user?.nickname?.toLowerCase().includes(searchLower) ||
                        order.user?.name?.toLowerCase().includes(searchLower)
                      );
                    }

                    return filteredOrders;
                  })
                )
            )
          ),
  });

  get ordersResource() {
    return this.ordersRxResource;
  }

  private refreshOrders() {
    this.manualRefresh.next();
  }

  onTakeout(order: OrderItem): void {
    this.orderRepository
      .updateOrder(order.id, OrderStateType.Fetched)
      .pipe(
        tap(() => this.refreshOrders()) // Refresh after update
      )
      .subscribe({
        next: () => {
          console.log(`Order ${order.id} marked as fetched/served.`);
        },
        error: (err) => {
          console.error('Error marking order as fetched/served:', err);
        },
      });
  }

  areAllPositionsReady(order: OrderItem): boolean {
    return order.positions.every((p) => p.ready);
  }

  markPositionReady(positionId: number, ready: boolean): void {
    this.orderRepository.updateOrderPosition(positionId, ready).subscribe({
      complete: () => {
        console.log(`Position ${positionId} ready state updated to ${ready}`);
        this.refreshOrders(); // Refresh to ensure data consistency
      },
      error: (err) => {
        console.error('Error updating order position:', err);
        // Consider reverting optimistic update here
        this.refreshOrders(); // Refresh to get authoritative state after error
      },
    });
  }

  updateOrderState(orderId: number, state: OrderStateType) {
    this.orderRepository.updateOrder(orderId, state).subscribe({
      next: () => {
        console.log(`Order ${orderId} marked as ${state}.`);
        this.refreshOrders();
      },
    });
  }

  sortOrders(orders: OrderItem[], sort: 'newest' | 'oldest' | 'pickupSoonest' | 'pickupLatest' | null) {
    if (sort === null) {
      return orders;
    }
    return orders.sort((a, b) => {
      switch (sort) {
        case 'newest':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'pickupSoonest':
          return this.compareTakeoutTime(a, b);
        case 'pickupLatest':
          return this.compareTakeoutTime(b, a);
      }
    });
  }

  /**
   * Compares two orders by their takeout time.
   * Orders without takeout_time (on-site orders) are sorted to the front (highest priority).
   */
  private compareTakeoutTime(a: OrderItem, b: OrderItem): number {
    // On-site orders (no takeout_time) have highest priority
    if (a.takeout_time === null && b.takeout_time === null) return 0;
    if (a.takeout_time === null) return -1;
    if (b.takeout_time === null) return 1;
    return new Date(a.takeout_time).getTime() - new Date(b.takeout_time).getTime();
  }

  /**
   * Handles filter menu interactions from the UI.
   */
  setFilter(filter: OrderFilterType) {
    switch (filter) {
      case 'current':
        this.showCurrent.set(!this.showCurrent());
        break;
      case 'past':
        this.showOutdated.set(!this.showOutdated());
        break;
      case 'canceled':
        this.toggleState(OrderStateType.Cancelled);
        break;
      case 'open':
        this.toggleState(OrderStateType.Open);
        break;
      case 'ready':
        this.toggleState(OrderStateType.Ready);
        break;
      case 'fetched':
        this.toggleState(OrderStateType.Fetched);
        break;
      case 'unclaimed':
        this.toggleState(OrderStateType.Unclaimed);
        break;
      case 'allStatesOn':
        this.selectedStates.set(new Set(Object.values(OrderStateType) as OrderStateType[]));
        break;
      case 'allStatesOff':
        this.selectedStates.set(new Set());
        break;
      case 'preorders':
        this.showPreorders.set(!this.showPreorders());
        break;
      case 'onSite':
        this.showOnSite.set(!this.showOnSite());
        break;
    }

    this.refreshOrders();
  }

  private toggleState(state: OrderStateType) {
    const set = new Set(this.selectedStates());
    if (set.has(state)) {
      set.delete(state);
    } else {
      set.add(state);
    }
    this.selectedStates.set(set);
  }

  setSortBy(sort: 'newest' | 'oldest' | 'pickupSoonest' | 'pickupLatest') {
    this.sortBy.set(sort);
  }

  /**
   * Loads filter settings from LocalStorage.
   * Returns default values if nothing is stored or parsing fails.
   */
  private loadFilterSettings(): OrderFilterSettings {
    const defaults: OrderFilterSettings = {
      showOutdated: false,
      showCurrent: true,
      showPreorders: true,
      showOnSite: true,
      sortBy: 'newest',
      selectedStates: [
        OrderStateType.Cancelled,
        OrderStateType.Open,
        OrderStateType.Ready,
        OrderStateType.Fetched,
        OrderStateType.Unclaimed,
      ],
    };

    try {
      const stored = localStorage.getItem(ORDERS_FILTER_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<OrderFilterSettings>;
        return { ...defaults, ...parsed };
      }
    } catch (e) {
      console.warn('Failed to load order filter settings from LocalStorage:', e);
    }

    return defaults;
  }

  /**
   * Saves current filter settings to LocalStorage.
   */
  private saveFilterSettings(): void {
    const settings: OrderFilterSettings = {
      showOutdated: this.showOutdated(),
      showCurrent: this.showCurrent(),
      showPreorders: this.showPreorders(),
      showOnSite: this.showOnSite(),
      sortBy: this.sortBy(),
      selectedStates: Array.from(this.selectedStates()),
    };

    try {
      localStorage.setItem(ORDERS_FILTER_STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
      console.warn('Failed to save order filter settings to LocalStorage:', e);
    }
  }

  // Effect to automatically save settings when any filter changes
  private persistenceEffect = effect(() => {
    // Access all signals to track them
    this.showOutdated();
    this.showCurrent();
    this.showPreorders();
    this.showOnSite();
    this.sortBy();
    this.selectedStates();

    // Save to LocalStorage
    this.saveFilterSettings();
  });
}
